import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  InputAdornment,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import CustomTabs from "../../../common/custom-tabs";
import AppTable from "../../../common/app-table";
import typography from "../../../cssUtils/typography";
import {
  ColorsConstants,
  borderColors,
  grayFontColor,
  inputColor,
} from "../../../cssUtils/constants";
import {
  GET_KALANJAY_BATCHES,
  GET_KALANJAY_CENTRES,
  GET_KALANJAY_BATCH_COLLECTION,
  BATCH_WISE_COLLECTION, // you may need to add this URL
} from "../../../config/urls";
import axios from "axios";
import { useAuth } from "../../../hooks/AuthProvider";

// ── shared field style (same as KalanjayBatches) ─────────────────────────────
const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: 40,
    ...typography.bodyLarge,
    color: inputColor,
    "& fieldset": { borderColor: borderColors.inputFieldGrey },
    "&:hover fieldset": { borderColor: ColorsConstants.primaryGray50 },
    "&.Mui-focused fieldset": { borderColor: ColorsConstants.primaryPurple100 },
  },
  "& .MuiInputLabel-root": { ...typography.bodyLarge },
};

// ── Summary Card ──────────────────────────────────────────────────────────────
const SummaryCard = ({ label, amount, studentCount, dotColor }) => (
  <Paper
    variant="outlined"
    sx={{
      width: "100%",
      minWidth: 0,
      p: 2.5,
      borderRadius: "12px",
      borderColor: borderColors.inputFieldGrey,
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
      {dotColor && (
        <Box
          sx={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            backgroundColor: dotColor,
            flexShrink: 0,
          }}
        />
      )}
      <Typography sx={{ ...typography.bodySmall, color: grayFontColor }}>
        {label}
      </Typography>
    </Box>
    <Typography
      sx={{
        ...typography.h1,
        color: dotColor || ColorsConstants.primaryPurple100,
        fontSize: "1.75rem",
        fontWeight: 700,
        lineHeight: 1.2,
      }}
    >
      ₹{Number(amount || 0).toLocaleString("en-IN")}
    </Typography>
    {studentCount !== undefined && (
      <Typography
        sx={{ ...typography.bodySmall, color: grayFontColor, mt: 0.5 }}
      >
        {studentCount} student{studentCount !== 1 ? "s" : ""}
        {label === "Total collected" ? " paid" : ""}
      </Typography>
    )}
  </Paper>
);

// ── Sortable column header ────────────────────────────────────────────────────
const SortableHeader = ({ label, field, sortField, sortDir, onSort }) => {
  const active = sortField === field;
  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        userSelect: "none",
        "&:hover": { opacity: 0.75 },
      }}
    >
      {label}
      {active ? (
        sortDir === "asc" ? (
          <ArrowUpwardIcon sx={{ fontSize: 14 }} />
        ) : (
          <ArrowDownwardIcon sx={{ fontSize: 14 }} />
        )
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 14, opacity: 0.35 }} />
      )}
    </Box>
  );
};

// ── helpers ───────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().slice(0, 10);
const startOfYear = () => `${new Date().getFullYear()}-01-01`;

const formatDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
};

// ── Main Component ────────────────────────────────────────────────────────────
const KalanjayBatchCollection = ({
  selectedBatch,
  setSelectedBatch,
  setActiveTab,
  setLastViewedPage
}) => {
const { getValidToken, permissions = [] } = useAuth();

const isAdmin =
  permissions.includes("payment_report.view");

const isManager =
  permissions.includes("payment_report.view_grant_road");

const grantRoadOnly = isManager; 
  const [centres, setCentres] = useState([]);
  const [centreTab, setCentreTab] = useState("");
  const [batches, setBatches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState("");
  const [batchesFromBatchesTable, setBatchesFromBatchesTable] = useState([]);
  // Date range
  const formatLocalDate = (date) => {
    const yr = date.getFullYear();
    const mn = String(date.getMonth() + 1).padStart(2, "0");
    
    const dt = String(date.getDate()).padStart(2, "0");
    return `${yr}-${mn}-${dt}`;
  };

  const today = new Date();

  const [fromDate, setFromDate] = useState(
    formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
  );

  const [toDate, setToDate] = useState(
    formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
  );

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Sorting
  const [sortField, setSortField] = useState("");
  const [sortDir, setSortDir] = useState("desc");

 const centreTabs = centres.map((c) => {
    if (isAdmin) {
        return { label: `${c.name} (${c.batches_count ?? 0})`, value: c.id, disabled: false };
    }
    if (isManager) {
        // Grant Road clickable, Juhu locked
        return { label: `${c.name} (${c.batches_count ?? 0})`, value: c.id, disabled: c.name === "Juhu" };
    }
    // other roles
    return { label: `${c.name} (${c.batches_count ?? 0})`, value: c.id, disabled: true };
});

  // ── derived summary ─────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const filtered = batches.filter((b) =>
      b.name?.toLowerCase().includes(search.toLowerCase()),
    );
    const totalAmount = filtered.reduce(
      (s, b) => s + Number(b.collection || 0),
      0,
    );
    const cashAmount = filtered.reduce(
      (s, b) => s + Number(b.cash_amount || 0),
      0,
    );
    const chequeAmount = filtered.reduce(
      (s, b) => s + Number(b.cheque_amount || 0),
      0,
    );
    const totalStudents = filtered.reduce(
      (s, b) => s + Number(b.total_students || 0),
      0,
    );
    const cashStudents = filtered.reduce(
      (s, b) => s + Number(b.cash_students || 0),
      0,
    );
    const chequeStudents = filtered.reduce(
      (s, b) => s + Number(b.cheque_students || 0),
      0,
    );
    return {
      totalAmount,
      cashAmount,
      chequeAmount,
      totalStudents,
      cashStudents,
      chequeStudents,
    };
  }, [batches, search]);

  // ── fetch ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchCentres();
  }, []);

  const handleSelectBatch = (batchId) => {
    const batchSelected = batchesFromBatchesTable.find((batch) => batch.id === Number(batchId))
    setSelectedBatch(batchSelected)
    setActiveTab("batches")
    setLastViewedPage("payment_report")
  }

  useEffect(() => {
    if (centreTab) {
      fetchBatchWiseCollection(centreTab);
    }
  }, [centreTab, fromDate, toDate]);

  useEffect(() => {
    if (centres.length > 0 && !centreTab) {
      const defaultCentre = grantRoadOnly
        ? centres.find((c) => c.name.toLowerCase().includes("grant road")) || centres[0]
        : centres[0];
      setCentreTab(defaultCentre.id);
      fetchBatchWiseCollection(defaultCentre.id);
    }
  }, [centres]);

  const fetchBatches = async (
    limit = 200,
    offset = 0,
    search = "",
    centreId = "",
  ) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const params = new URLSearchParams({ limit, offset });
      if (search) params.append("search", search);
      if (centreId) params.append("centre", centreId);

      const res = await axios.get(
        `${GET_KALANJAY_BATCHES}?${params.toString()}`,
        { headers },
      );
      const batchesFromApi = res?.data?.results || res.data;
      const count = res?.data?.count ?? batchesFromApi.length;
      setBatchesFromBatchesTable(batchesFromApi);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBatchWiseCollection = async (centreId = centreTab) => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const params = new URLSearchParams();

      if (centreId) params.append("centre", centreId);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);

      const res = await axios.get(
        `${BATCH_WISE_COLLECTION}?${params.toString()}`,
        { headers },
      );

      const results = res?.data?.results || [];

      setBatches(results);
      setTotalCount(res?.data?.count ?? results.length);
      console.log(res?.data?.centres, "centres in batch collection response"); // debug log
      // setCentres(res?.data?.centres || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchCentres = async () => {
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };
try {
  const res = await axios.get(GET_KALANJAY_CENTRES, { headers });

  console.log("CENTRES API", res?.data?.results);

  setCentres(res?.data?.results || []);
} catch (err) {
  console.error(err);
}
  };

  // ── handlers ────────────────────────────────────────────────────────────────
  const handleCentreChange = (centreId) => {
    setCentreTab(centreId);
    setSearch("");
    setPage(0);
    fetchBatchWiseCollection(centreId);
  };

  const handleSearchChange = (val) => {
    setSearch(val);
  };

  const handleDateChange = (field, val) => {
    const next = { from: fromDate, to: toDate, [field]: val };
    setFromDate(next.from);
    setToDate(next.to);
    setPage(0);
    fetchBatchWiseCollection(centreTab);
  };

  const handleSort = (field) => {
    setSortDir((d) => (sortField === field && d === "desc" ? "asc" : "desc"));
    setSortField(field);
  };

  const onPageChange = (_, newPage) => {
    setPage(newPage);
  };

  const onRowsPerPageChange = (e) => {
    const val = Number(e?.target?.value);
    if (!val || isNaN(val)) return;
    setRowsPerPage(val);
    setPage(0);
  };

  // ── sorted + filtered rows ──────────────────────────────────────────────────
  const displayRows = useMemo(() => {
    let rows = batches.filter((b) =>
      b.name?.toLowerCase().includes(search.toLowerCase()),
    );
    if (sortField) {
      rows = [...rows].sort((a, b) => {
        const aVal = Number(a[sortField] ?? 0);
        const bVal = Number(b[sortField] ?? 0);
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      });
    }
    return rows;
  }, [batches, search, sortField, sortDir]);

  // ── table columns ───────────────────────────────────────────────────────────
  const columns = [
    {
      id: "name",
      label: "Batch", 
      render: (row) => (
        <Typography
          sx={{
            ...typography.bodyLarge,
            color: ColorsConstants.primaryPurple100,
            fontWeight: 500,
            cursor: "pointer",
            textDecoration: "underline"
          }}
          onClick={() => {console.log(row, "selected row"); handleSelectBatch(row.id)}}
        >
          {row.name}
        </Typography>
      ),
    },
    {
      id: "cash_amount",
      label: (
        <SortableHeader
          label="Cash Amount"
          field="cash_amount"
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />
      ),
      render: (row) => (
        <Typography
          sx={{ ...typography.bodyLarge, color: "#008026", fontWeight: 500 }}
        >
          {row.cash_amount
            ? `₹${Number(row.cash_amount).toLocaleString("en-IN")}`
            : "—"}
        </Typography>
      ),
    },
    {
      id: "cash_students",
      label: "Cash Students",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#008026" }}>
          {row.cash_students ?? "—"}
        </Typography>
      ),
    },
    {
      id: "cheque_amount",
      label: (
        <SortableHeader
          label="Cheque Amount"
          field="cheque_amount"
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />
      ),
      render: (row) => (
        <Typography
          sx={{ ...typography.bodyLarge, color: "#b45309", fontWeight: 500 }}
        >
          {row.cheque_amount
            ? `₹${Number(row.cheque_amount).toLocaleString("en-IN")}`
            : "—"}
        </Typography>
      ),
    },
    {
      id: "cheque_students",
      label: "Cheque Students",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, color: "#b45309" }}>
          {row.cheque_students ?? "—"}
        </Typography>
      ),
    },
    {
      id: "total_students",
      label: (
        <SortableHeader
          label="Total Students"
          field="total_students"
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />
      ),
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
          {row.total_students ?? "—"}
        </Typography>
      ),
    },
    {
      id: "collection",
      label: (
        <SortableHeader
          label="Total Amount"
          field="collection"
          sortField={sortField}
          sortDir={sortDir}
          onSort={handleSort}
        />
      ),
      align: "right",
      render: (row) => (
        <Typography sx={{ ...typography.bodyLarge, fontWeight: 700 }}>
          {row.collection
            ? `₹${Number(row.collection).toLocaleString("en-IN")}`
            : "—"}
        </Typography>
      ),
    },
  ];

  // ── render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Tabs + Date Range */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <CustomTabs
          value={centreTab}
          onChange={(_, v) => handleCentreChange(v)}
          tabs={centreTabs}
          sx={{ width: "100%", maxWidth: "100%", flex: 1, minWidth: 0 }}
        />

        {/* Date pickers */}
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, alignItems: { xs: "stretch", sm: "center" }, gap: 2, ml: { xs: 0, md: "auto" }, width: { xs: "100%", lg: "auto" } }}>
          <Box>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: grayFontColor,
                mb: 0.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.65rem",
              }}
            >
              FROM
            </Typography>
            <TextField
              type="date"
              size="small"
              value={fromDate}
              onChange={(e) => handleDateChange("from", e.target.value)}
              inputProps={{ max: toDate }}
              sx={{ ...fieldSx, width: { xs: "100%", md: 160, sm: 180 } }}
            />
          </Box>
          <Box>
            <Typography
              sx={{
                ...typography.bodySmall,
                color: grayFontColor,
                mb: 0.5,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                fontSize: "0.65rem",
              }}
            >
              TO
            </Typography>
            <TextField
              type="date"
              size="small"
              value={toDate}
              onChange={(e) => handleDateChange("to", e.target.value)}
              inputProps={{ min: fromDate }}
              sx={{ ...fieldSx, width: { xs: "100%", sm: 180, md: 160 } }}
            />
          </Box>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", lg: "repeat(3,1fr)" }, gap: 2, mb: 3 }}>
        <SummaryCard
          label="Total collected"
          amount={summary.totalAmount}
          studentCount={summary.totalStudents}
        />
        <SummaryCard
          label="Cash"
          amount={summary.cashAmount}
          studentCount={summary.cashStudents}
          dotColor="#008026"
        />
        <SummaryCard
          label="Cheque"
          amount={summary.chequeAmount}
          studentCount={summary.chequeStudents}
          dotColor="#b45309"
        />
      </Box>

      {/* Batch Breakdown Table */}
      <Paper
        variant="outlined"
        sx={{
          borderRadius: "12px",
          borderColor: borderColors.inputFieldGrey,
          mb: 3,
        }}
      >
        {/* Table Header Row */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: { xs: "flex-start", md: "space-between" },
            px: 2,
            py: 1.5,
            borderBottom: `1px solid ${borderColors.inputFieldGrey}`,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Typography sx={{ ...typography.bodyLarge, fontWeight: 600 }}>
            Batch breakdown{" "}
            <Typography
              component="span"
              sx={{ ...typography.bodySmall, color: grayFontColor }}
            >
              ({displayRows.length} batch{displayRows.length !== 1 ? "es" : ""})
            </Typography>
          </Typography>

          <TextField
            size="small"
            placeholder="Search batch…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: grayFontColor, fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            sx={{ ...fieldSx, width: { xs: "100%", sm: 250, md: 220 } }}
          />
        </Box>

      </Paper>
        <AppTable
          columns={columns}
          rows={displayRows}
          rowKey="id"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[20, 50, 100]}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
          emptyMessage={
            search
              ? "No batches match your search."
              : "No batch data available."
          }
        />
    </Box>
  );
};

export default KalanjayBatchCollection;
